import React, { createElement, memo, useMemo, useState } from "react";
import classNames from "classnames";

import { logAnalyticsEvent, setAnalyticsUserProperties } from "@/constants/amplitude";
import { WebEventProps, WebEventTypes } from "@/constants/event";
import i18n from "@/localization/i18next";
import { Question } from "@/types/question";

import useAnswers from "../../utils/useAnswers";

interface QuestionProps {
    question: Question;
    handleNext(answ: unknown): void;
    isActive: boolean;
}

const Rate = ({ question, handleNext, isActive }: QuestionProps) => {
    const [selectedAnsId, setSelectedAnsId] = useState<number | null>(null);

    const answers = useAnswers();

    const titleElement = useMemo(() => {
        const title = question?.title;

        if (typeof title === "function") {
            return createElement(title, { answers });
        }

        return title;
    }, [answers, question]);

    if (!question) return null;

    const goToNextQuestion = (ansId: number) => {
        handleNext({ [question.id]: ansId });
        logAnalyticsEvent(WebEventTypes.RESULT_EVENT, {
            [WebEventProps.SCREEN]: question.event,
            [WebEventProps.RESULT]: ansId,
        });
        setAnalyticsUserProperties({ [question.id]: ansId });
    };

    const handleContinue = (ansId: number) => () => {
        setSelectedAnsId(ansId);
        goToNextQuestion(ansId);
    };

    const renderRates = () => {
        return Array.from({ length: question.maxRate ?? 0 }, (_, index) => index + 1).map(
            (rate) => (
                <div
                    key={rate}
                    className={classNames(
                        "flex aspect-[0.95/1] items-center justify-center rounded-2xl px-5 py-4 text-zing-18-26 font-semibold text-white hover:cursor-pointer",
                        {
                            "bg-zing-blue-200 female:bg-zing-warm-tangerine-400 female:text-white":
                                rate === selectedAnsId,
                            "bg-zing-black-200 female:bg-zing-warm-tangerine-100 female:text-black":
                                rate !== selectedAnsId,
                            "pointer-events-none": !isActive,
                        }
                    )}
                    onClick={handleContinue(rate)}
                >
                    {rate}
                </div>
            )
        );
    };

    return (
        <div className="grid h-full content-start gap-10 pb-6">
            <div className="grid gap-4 text-white female:text-black">
                <div className={classNames("whitespace-pre-line text-zing-24-32 font-semibold")}>
                    {titleElement}
                </div>
                <div className="rounded-2xl bg-zing-blue-800 p-4 text-center text-zing-18-26 female:bg-zing-warm-yellow-200">
                    "{question.subtitle}"
                </div>
            </div>
            <div className="grid gap-2">
                <div className={classNames("grid grid-flow-col grid-cols-[repeat(5,_1fr)] gap-2")}>
                    {renderRates()}
                </div>
                <div className="flex justify-between text-zing-12-18 text-zing-gray-400">
                    <span>{i18n.t("not_at_all")}</span>
                    <span>{i18n.t("completely")}</span>
                </div>
            </div>
        </div>
    );
};

export default memo(Rate);
